/* @jsx h */
import { defineComponent, h, ref, onMounted } from "vue";
import { RouterLink } from "vue-router";
import logoImageURL from "@lemmonhq/assets/images/branding/dark-favico.svg";
import type { ProductNutritionalInformation } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

import type {
  MenuCreatorCategory,
  MenuCreatorProduct,
  CategoryFormData,
  ProductFormData,
} from "./types";
import { getSampleData } from "./sampleData";
import {
  NutritionForm,
  ProductForm,
  ProductDetailModal,
  EditMenuComponent,
  PreviewMenuComponent,
} from "./components";
import { PublicHeaderComponent } from "../../components/PublicHeaderComponent";
import { SeoHead } from "../../components/SeoHead";
import { CustomerLanguageSelector } from "../../components/CustomerLanguageSelector";

export const MenuCreatorPage = defineComponent({
  setup() {
    const categories = ref<MenuCreatorCategory[]>([]);
    const selectedProduct = ref<MenuCreatorProduct | null>(null);
    const showProductDetail = ref(false);
    const editingCategory = ref<MenuCreatorCategory | null>(null);
    const editingProduct = ref<MenuCreatorProduct | null>(null);
    const showCategoryForm = ref(false);
    const showProductForm = ref(false);
    const showNutritionForm = ref(false);
    const activeTab = ref<"menu" | "preview">("menu");
    const { t } = useLocalization();

    // Form states
    const categoryForm = ref<CategoryFormData>({
      name: "",
      order: 0,
      status: "active",
      color: "#3B82F6",
    });

    const productForm = ref<ProductFormData>({
      name: "",
      description: "",
      price: "",
      weight: "",
      type: "food",
      item_type: "food",
      status: "active",
    });

    const nutritionForm = ref<ProductNutritionalInformation>({
      servingSize: {
        amount: 100,
        unit: "g",
      },
      calories: 0,
      macronutrients: {
        totalFat: { value: 0, unit: "g" },
        totalCarbohydrate: { value: 0, unit: "g" },
        protein: { value: 0, unit: "g" },
      },
      allergens: [],
      dietaryTags: [],
    });

    // Initialize with sample data
    onMounted(() => {
      categories.value = getSampleData();
    });

    // Helper functions
    const generateId = () => Math.random().toString(36).substr(2, 9);

    const addCategory = () => {
      editingCategory.value = null;
      categoryForm.value = {
        name: "",
        order: categories.value.length,
        status: "active",
        color: "#3B82F6",
      };
      showCategoryForm.value = true;
    };

    const editCategory = (category: MenuCreatorCategory) => {
      editingCategory.value = category;
      categoryForm.value = {
        name: category.name,
        order: category.order,
        status: category.status,
        color: category.color || "#3B82F6",
      };
      showCategoryForm.value = true;
    };

    const saveCategory = () => {
      const categoryData = {
        ...categoryForm.value,
        id: editingCategory.value?.id || generateId(),
        guid: editingCategory.value?.guid || generateId(),
        product_guids: editingCategory.value?.product_guids || [],
        product_ids: editingCategory.value?.product_ids || [],
        deleted_at: null,
        products: editingCategory.value?.products || [],
      };

      if (editingCategory.value) {
        const index = categories.value.findIndex(
          (c) => c.id === editingCategory.value!.id,
        );
        categories.value[index] = {
          ...categories.value[index],
          ...categoryData,
        };
      } else {
        categories.value.push(categoryData as MenuCreatorCategory);
      }

      showCategoryForm.value = false;
      editingCategory.value = null;
    };

    const deleteCategory = (categoryId: string) => {
      categories.value = categories.value.filter((c) => c.id !== categoryId);
    };

    const addProduct = (categoryId: string) => {
      editingProduct.value = null;
      productForm.value = {
        name: "",
        description: "",
        price: "",
        weight: "",
        type: "food",
        item_type: "food",
        status: "active",
      };
      showProductForm.value = true;
      (productForm.value as any).categoryId = categoryId;
    };

    const editProduct = (product: MenuCreatorProduct) => {
      editingProduct.value = product;
      productForm.value = {
        name: product.name,
        description: product.description || "",
        price: product.price,
        weight: product.weight || "",
        type: product.type,
        item_type: product.item_type || "food",
        status: product.status,
      };
      showProductForm.value = true;
    };

    const saveProduct = () => {
      const categoryId =
        (productForm.value as any).categoryId || editingProduct.value?.id;
      const category = categories.value.find(
        (c) =>
          c.id === categoryId ||
          c.products.some((p) => p.id === editingProduct.value?.id),
      );

      if (!category) return;

      const productData = {
        ...productForm.value,
        id: editingProduct.value?.id || generateId(),
        slug: (productForm.value.name || "").toLowerCase().replace(/\s+/g, "-"),
        bundles: editingProduct.value?.bundles || null,
        weight: productForm.value.weight || null,
        additional_info: null,
        special_price: null,
        is_custom: 0,
        deleted_at: null,
        extra_limit: null,
        product_limit: null,
        color: editingProduct.value?.color,
        images: editingProduct.value?.images,
        image_list: editingProduct.value?.image_list,
        nutritional_info: editingProduct.value?.nutritional_info,
        sample_image:
          editingProduct.value?.sample_image ||
          "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=300&fit=crop",
      };

      if (editingProduct.value) {
        const productIndex = category.products.findIndex(
          (p) => p.id === editingProduct.value!.id,
        );
        category.products[productIndex] = {
          ...category.products[productIndex],
          ...productData,
        };
      } else {
        category.products.push(productData as MenuCreatorProduct);
      }

      showProductForm.value = false;
      editingProduct.value = null;
    };

    const deleteProduct = (categoryId: string, productId: string) => {
      const category = categories.value.find((c) => c.id === categoryId);
      if (category) {
        category.products = category.products.filter((p) => p.id !== productId);
      }
    };

    const editNutrition = (product: MenuCreatorProduct) => {
      editingProduct.value = product;
      nutritionForm.value = product.nutritional_info || {
        servingSize: { amount: 100, unit: "g" },
        calories: 0,
        macronutrients: {
          totalFat: { value: 0, unit: "g" },
          totalCarbohydrate: { value: 0, unit: "g" },
          protein: { value: 0, unit: "g" },
        },
        allergens: [],
        dietaryTags: [],
      };
      showNutritionForm.value = true;
    };

    const saveNutrition = (nutrition: ProductNutritionalInformation) => {
      if (editingProduct.value) {
        editingProduct.value.nutritional_info = { ...nutrition };
      }
      showNutritionForm.value = false;
      editingProduct.value = null;
    };

    const showProductDetails = (product: MenuCreatorProduct) => {
      selectedProduct.value = product;
      showProductDetail.value = true;
    };

    const updateProductFormField = (
      field: keyof ProductFormData,
      value: string,
    ) => {
      (productForm.value as any)[field] = value;
    };

    const menuCreatorStructuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: t("customer.menu_creator.title"),
      description: t("customer.menu_creator.seo_description"),
      url: typeof window !== "undefined" ? window.location.href : "",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };

    return () => (
      <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <SeoHead
          title={t("customer.menu_creator.seo_title")}
          description={t("customer.menu_creator.seo_description")}
          keywords={t("customer.menu_creator.seo_keywords")}
          type="website"
          structuredData={menuCreatorStructuredData}
        />
        <PublicHeaderComponent
          title={t("customer.menu_creator.title")}
          subtitle={t("customer.menu_creator.subtitle")}
        >
          <div class="flex flex-wrap items-center gap-3">
            <CustomerLanguageSelector languageIds={["fr", "de", "it", "en"]} />
            <div class="flex bg-gray-100 rounded-lg p-1">
              <button
                class={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab.value === "menu"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => (activeTab.value = "menu")}
              >
                <span class="material-symbols-outlined text-sm mr-2 align-text-bottom">
                  edit
                </span>
                {t("customer.menu_creator.edit_menu")}
              </button>
              <button
                class={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab.value === "preview"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => (activeTab.value = "preview")}
              >
                <span class="material-symbols-outlined text-sm mr-2 align-text-bottom">
                  visibility
                </span>
                {t("customer.menu_creator.preview")}
              </button>
            </div>
          </div>
        </PublicHeaderComponent>

        <main class="max-w-7xl mx-auto px-4 py-12">
          {activeTab.value === "menu" ? (
            <EditMenuComponent
              categories={categories.value}
              onAddCategory={addCategory}
              onAddProduct={addProduct}
              onEditCategory={editCategory}
              onDeleteCategory={deleteCategory}
              onEditProduct={editProduct}
              onDeleteProduct={deleteProduct}
              onEditNutrition={editNutrition}
              onShowProductDetails={showProductDetails}
            />
          ) : (
            <PreviewMenuComponent
              categories={categories.value}
              onShowProductDetails={showProductDetails}
            />
          )}
        </main>

        {/* Enhanced Category Form Modal */}
        {showCategoryForm.value && (
          <div class="modal modal-open">
            <div class="modal-box max-w-lg">
              <h3 class="font-serif text-2xl font-light mb-6">
                {editingCategory.value
                  ? t("customer.menu_creator.edit_category")
                  : t("customer.menu_creator.create_category")}
              </h3>

              <div class="space-y-6">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">
                      {t("customer.menu_creator.category_name")}
                    </span>
                  </label>
                  <input
                    type="text"
                    value={categoryForm.value.name}
                    onInput={(e) =>
                      (categoryForm.value.name = (
                        e.target as HTMLInputElement
                      ).value)
                    }
                    class="input input-bordered input-lg"
                    placeholder={t(
                      "customer.menu_creator.category_placeholder",
                    )}
                  />
                </div>

                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">
                      {t("customer.menu_creator.brand_color")}
                    </span>
                  </label>
                  <div class="flex gap-3">
                    <input
                      type="color"
                      value={categoryForm.value.color}
                      onInput={(e) =>
                        (categoryForm.value.color = (
                          e.target as HTMLInputElement
                        ).value)
                      }
                      class="input input-bordered h-12 w-20 p-1"
                    />
                    <input
                      type="text"
                      value={categoryForm.value.color}
                      onInput={(e) =>
                        (categoryForm.value.color = (
                          e.target as HTMLInputElement
                        ).value)
                      }
                      class="input input-bordered flex-1"
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">
                      {t("customer.menu_creator.display_order")}
                    </span>
                  </label>
                  <input
                    type="number"
                    value={categoryForm.value.order}
                    onInput={(e) =>
                      (categoryForm.value.order =
                        parseInt((e.target as HTMLInputElement).value) || 0)
                    }
                    class="input input-bordered"
                    min="0"
                  />
                </div>
              </div>

              <div class="modal-action mt-8">
                <button
                  onClick={() => (showCategoryForm.value = false)}
                  class="btn btn-ghost"
                >
                  {t("customer.account.cancel")}
                </button>
                <button
                  onClick={saveCategory}
                  class="btn bg-gray-900 text-white hover:bg-gray-800"
                  disabled={!categoryForm.value.name.trim()}
                >
                  {editingCategory.value
                    ? t("customer.menu_creator.update_category")
                    : t("customer.menu_creator.create_category_button")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Product Form Modal */}
        <ProductForm
          isOpen={showProductForm.value}
          editingProduct={editingProduct.value}
          productForm={productForm.value}
          onSave={saveProduct}
          onClose={() => (showProductForm.value = false)}
          onFormUpdate={updateProductFormField}
        />

        {/* Enhanced Nutrition Form Modal */}
        <NutritionForm
          isOpen={showNutritionForm.value}
          nutritionData={nutritionForm.value}
          onSave={saveNutrition}
          onClose={() => (showNutritionForm.value = false)}
        />

        {/* Enhanced Product Detail Modal */}
        {selectedProduct.value && (
          <ProductDetailModal
            isOpen={showProductDetail.value}
            product={selectedProduct.value}
            onClose={() => {
              showProductDetail.value = false;
              selectedProduct.value = null;
            }}
          />
        )}
      </div>
    );
  },
});
